/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import { formatUTCDate } from '../../utils/utils';
import UserSegmentToggle from '../common/UserSegmentToggle';
import { useUserSegment, watchFilterValue } from '../../context/UserSegmentContext';
import ExportButton from '../active-users/ExportButton';
import DateRangeBar, { fmt, presetRange } from '../active-users/DateRangeBar';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import SleepSummarySummaryCards from './SleepSummarySummaryCards';
import SleepSummaryTrendChart from './SleepSummaryTrendChart';
import SleepSummaryPlatformMixPanel from './SleepSummaryPlatformMixPanel';
import { readSavedFilters, usePersistFilters } from '../../hooks/filterPersistence';
import '../active-users/active-users.css';
import './SleepSummary.css';

const LIMIT = 20;
const STATE_KEY = 'sleepSummaryListState';

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'country', label: 'Country' },
  { key: 'mobile_app_version', label: 'App Version' },
  { key: 'subscription_status', label: 'Subscription' },
  { key: 'uuid_count', label: 'Total Sessions' },
  { key: 'platforms', label: 'Platforms' },
  { key: 'last_synced', label: 'Last Synced' },
];

const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

const SleepSummaryList = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();
  const userSegment = watchFilterValue(segment) || 'all';

  // auth
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  // ui
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // data
  const [users, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [totalSessions, setTotalSessions] = useState(0);
  const [subscribedCount, setSubscribedCount] = useState(0);
  const [page, setPage] = useState(1);
  // filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState('last30');
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  // analytics
  const [summary, setSummary] = useState({ current: {}, prior: {}, deltas: {} });
  const [trend, setTrend] = useState([]);
  const [platformMix, setPlatformMix] = useState({ totalUsers: 0, buckets: [] });

  const didMountSegment = useRef(false);

  // Mount: restore the view on return from a drill-down (Back), else seed Last-30.
  useEffect(() => {
    const saved = readSavedFilters(STATE_KEY);
    if (saved) {
      setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
      setDisplayFromDate(saved.fromDate ? new Date(`${saved.fromDate}T00:00:00`) : null);
      setDisplayToDate(saved.toDate ? new Date(`${saved.toDate}T00:00:00`) : null);
      setPreset(saved.preset || null);
      setSearchValue(saved.searchValue || '');
      setSubscriptionFilter(saved.subscriptionFilter || '');
      setSortField(saved.sortField || ''); setSortOrder(saved.sortOrder || '');
      setPage(saved.page || 1);
    } else {
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(`${fromStr}T00:00:00`));
      setDisplayToDate(new Date(`${toStr}T00:00:00`));
    }
    refreshToken();
  }, []);

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  }, [token]);

  // Segment change → full refetch (skip initial mount).
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  }, [segment]);

  // Snapshot filters so the exact view is restored on Back (remount).
  usePersistFilters(STATE_KEY, {
    fromDate, toDate, preset, searchValue, subscriptionFilter, sortField, sortOrder, page,
  });

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const qs = (p, f, t, s, sf, so, sub) =>
    `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&sortField=${sf}&sortOrder=${so}&subscription_status=${sub}&user_segment=${userSegment}`;
  const rangeQs = (f, t, s, sub) =>
    `from_date=${f}&to_date=${t}&search=${s}&subscription_status=${sub}&user_segment=${userSegment}`;

  const applyList = (d) => {
    setData(d.data || []);
    setTotalItem(d.totalItem || 0);
    setTotalUsers(d.totalUsers || 0);
    setTotalUserBase(d.totalUserCount || 0);
    setTotalSessions(d.totalSessions || 0);
    setSubscribedCount(d.subscribedCount || 0);
  };
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  // Pagination + sort → list only.
  const getList = async (p, f, t, s, sf, so, sub) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/user-sleep-summary?${qs(p, f, t, s, sf, so, sub)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  // Filters/segment/preset/search/clear → list + summary + trend + platform-mix.
  const loadAll = async (p, f, t, s, sf, so, sub) => {
    setLoading(true); setError('');
    const range = rangeQs(f, t, s, sub);
    try {
      const [list, sum, tr, mix] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/user-sleep-summary?${qs(p, f, t, s, sf, so, sub)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/user-sleep-summary-summary?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/user-sleep-summary-trend?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/user-sleep-summary-platform-mix?${range}`, auth()),
      ]);
      applyList(list.data);
      setSummary(sum.data.data || { current: {}, prior: {}, deltas: {} });
      setTrend(tr.data.data || []);
      setPlatformMix(mix.data.data || { totalUsers: 0, buckets: [] });
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (n) => { setPage(n); getList(n, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter); };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key) => {
    const { fromStr, toStr } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFromDate(fromStr ? new Date(`${fromStr}T00:00:00`) : null);
    setDisplayToDate(toStr ? new Date(`${toStr}T00:00:00`) : null);
    setPage(1);
    loadAll(1, fromStr, toStr, searchValue, sortField, sortOrder, subscriptionFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter); };
  const handleSubscription = (e) => {
    const v = e.target.value; setSubscriptionFilter(v); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, v);
  };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setPreset(null); setSearchValue(''); setSubscriptionFilter(''); setSortField(''); setSortOrder('');
    setPage(1);
    loadAll(1, '', '', '', '', '', '');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, field, order, subscriptionFilter);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/user-sleep-summary-export?from_date=${fromDate}&to_date=${toDate}&search=${searchValue}&sortField=${sortField}&sortOrder=${sortOrder}&subscription_status=${subscriptionFilter}&user_segment=${userSegment}`,
      auth());
    return (res.data.data || []).map((r) => ({
      ...r,
      last_synced: r.last_synced ? formatUTCDate(r.last_synced) : '',
    }));
  };

  const drill = (id) => { navigate(`/user-sleep-summary-details/${btoa(String(id))}`); };
  const hasRange = !!(fromDate || toDate || searchValue || subscriptionFilter);

  const totalUsersVal = summary.current?.total_users ?? totalUsers;
  const totalSessionsVal = summary.current?.total_sessions ?? totalSessions;

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
    </th>
  );

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Health › <b>Sleep summary</b></span>
            <span className="au-route">/user-sleep-summary</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Sleep Summary</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load sleep summary. Please try again." onRetry={() => loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter)} />
          ) : (
            <>
              <SleepSummarySummaryCards
                totalUsers={totalUsersVal}
                totalSessions={totalSessionsVal}
                totalUserBase={totalUserBase}
                subscribedCount={subscribedCount}
                deltas={summary.deltas}
              />

              <SleepSummaryTrendChart data={trend} />

              <SleepSummaryPlatformMixPanel totalUsers={platformMix.totalUsers} buckets={platformMix.buckets} />

              <DateRangeBar
                leading={(
                  <>
                    <input
                      className="au-input" type="text" placeholder="Search by name or email"
                      value={searchValue} onChange={(e) => setSearchValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                      aria-label="Search users"
                    />
                    <select className="au-select" value={subscriptionFilter} onChange={handleSubscription} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                  </>
                )}
                preset={preset} displayFrom={displayFromDate} displayTo={displayToDate}
                hasRange={hasRange} onPreset={handlePreset} onFromChange={handleFromChange}
                onToChange={handleToChange} onSearch={handleSearch} onClear={clearFilter}
              />

              <p className="ss-range-note" data-testid="ss-range-note">
                Filtered by the sync date (created timestamp, UTC).{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all synced sleep (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : users.length === 0 ? (
                <EmptyState
                  title="No sleep data in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No users match the current filters.' : 'There is no synced sleep data to show yet.'}
                />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Per-user sleep summary</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="sleep-summary.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table ss-table" data-testid="ss-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Country</th>
                          <th>App Version</th><th>Subscription</th>
                          <SortTh field="uuid_count" label="Total Sessions" />
                          <th>Platforms</th>
                          <SortTh field="last_synced" label="Last Synced" />
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((row, index) => (
                          <tr key={`${row.id}-${index}`}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{row.name}</td>
                            <td data-label="Email" className="au-ellipsis">{row.email}</td>
                            <td data-label="Country">{row.country}</td>
                            <td data-label="App Version">{row.mobile_app_version}</td>
                            <td data-label="Subscription">{row.subscription_status}</td>
                            <td data-label="Total Sessions" className="tnum">
                              <Link
                                className="au-link" to={`/user-sleep-summary-details/${btoa(String(row.id))}`}
                                onClick={(e) => { e.preventDefault(); drill(row.id); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drill(row.id); } }}
                              >
                                {row.uuid_count}
                              </Link>
                            </td>
                            <td data-label="Platforms">{row.platforms}</td>
                            <td data-label="Last Synced" className="tnum">{row.last_synced ? formatUTCDate(row.last_synced) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination
                      activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem}
                      pageRangeDisplayed={5} onChange={handlePageChange}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SleepSummaryList;
